import { useEffect } from "react";
import { createPortal } from "react-dom";
import { EventShareButtons } from "./EventShareButtons";
import { CheckInForm } from "./forms/CheckInForm";
import { RsvpForm } from "./forms/RsvpForm";
import { useRsvpReservation } from "../hooks/useRsvpReservation";

type Props = {
  open: boolean;
  onClose: () => void;
  eventId?: number | null;
  onSuccess?: () => void;
  thanksLine?: string;
  title?: string;
  shareUrl?: string;
  shareDateIso?: string;
  shareEventTitle?: string | null;
  /** While the overnight is live, the homepage CTA checks the guest in online. */
  mode?: "rsvp" | "check-in";
};

export function RsvpModal({
  open,
  onClose,
  eventId,
  onSuccess,
  thanksLine,
  title,
  shareUrl,
  shareDateIso,
  shareEventTitle,
  mode = "rsvp",
}: Props) {
  const reservation = useRsvpReservation(eventId);
  const checkIn = mode === "check-in";
  const reservedTitle = checkIn
    ? reservation?.checkedInAt
      ? reservation.name.trim()
        ? `${reservation.name.trim()}, you're watching`
        : "You're checked in"
      : null
    : reservation
      ? reservation.name.trim()
        ? `${reservation.name.trim()}, you're reserved`
        : "You're reserved"
      : null;
  const heading =
    reservedTitle ??
    title ??
    (checkIn ? "Check in to watch" : "Reserve your seat");
  const eyebrow = checkIn
    ? "Check-in · online"
    : reservation
      ? `RSVP · ${reservation.seats} seat${reservation.seats === 1 ? "" : "s"}`
      : "RSVP";

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="rsvp-modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="rsvp-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="rsvp-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="rsvp-modal-header">
          <div>
            <div className="sec-eyebrow">
              <em>{eyebrow}</em>
            </div>
            <h2 id="rsvp-modal-title" className="sec-title sec-title-sm">
              {heading}
            </h2>
          </div>
          <button
            type="button"
            className="rsvp-modal-close"
            onClick={onClose}
            aria-label={checkIn ? "Close check-in form" : "Close RSVP form"}
          >
            ×
          </button>
        </header>
        <EventShareButtons
          url={shareUrl}
          dateIso={shareDateIso}
          eventTitle={shareEventTitle}
        />
        {checkIn ? (
          <CheckInForm eventId={eventId} onSuccess={onSuccess} />
        ) : (
          <RsvpForm eventId={eventId} onSuccess={onSuccess} thanksLine={thanksLine} />
        )}
      </div>
    </div>,
    document.body,
  );
}
